import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { tagSpecsToUniqueNames } from 'Core-utils/helpers/tagSpecList.util';
import { useTagSpecsCatalog } from 'Hooks/useTagSpecsCatalog';
import { FILTER_STYLES } from '../../AgToolingComplianceGrid/AgGridCompliance.config';

export type TagFilterModel = {
    filterType: 'tagMultiSelect';
    values: string[];
} | null;

export const TagColumnFilter = ({ model, onModelChange }: CustomFilterProps<any, any, TagFilterModel>) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const hidePopupRef = useRef<(() => void) | null>(null);
    const { tagSpecs, isLoading: catalogLoading } = useTagSpecsCatalog({ includeGlobals: true });
    const allTags = useMemo(() => tagSpecsToUniqueNames(tagSpecs), [tagSpecs]);
    const loading = catalogLoading;
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>(model?.values ?? []);
    const [showDropdown, setShowDropdown] = useState(false);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [searchFocused, setSearchFocused] = useState(false);
    const selectedRef = useRef<string[]>(model?.values ?? []);

    useEffect(() => {
        const vals = model?.values ?? [];
        setSelected(vals);
        selectedRef.current = vals;
    }, [model]);

    const doesFilterPass = useCallback((params: { data?: any }) => {
        if (selectedRef.current.length === 0) {
            return true;
        }
        const raw = params.data?.finding_tags;
        const findingTags = Array.isArray(raw) ? raw.filter(Boolean) : [];
        return findingTags.some((tag) => selectedRef.current.includes(tag));
    }, []);
    const afterGuiAttached = useCallback((params?: { hidePopup?: () => void }) => {
        hidePopupRef.current = params?.hidePopup ?? null;
        searchRef.current?.focus();
    }, []);
    useGridFilter({ doesFilterPass, afterGuiAttached });

    const suggestions = allTags.filter((t) => t.toLowerCase().includes(search.toLowerCase()) && !selected.includes(t));

    const addTag = (tag: string) => {
        setSelected((prev) => [...prev, tag]);
        setSearch('');
        setShowDropdown(false);
        searchRef.current?.focus();
    };

    const removeTag = (tag: string) => {
        setSelected((prev) => prev.filter((t) => t !== tag));
    };

    const handleApply = () => {
        selectedRef.current = selected;
        onModelChange(selected.length > 0 ? { filterType: 'tagMultiSelect', values: selected } : null);
        hidePopupRef.current?.();
    };

    const handleReset = () => {
        selectedRef.current = [];
        setSelected([]);
        setSearch('');
        onModelChange(null);
        hidePopupRef.current?.();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setShowDropdown(true);
    };

    const handleSearchFocus = () => {
        setShowDropdown(true);
        setSearchFocused(true);
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setShowDropdown(false);
        }, 150);
        setSearchFocused(false);
    };

    const handleItemMouseDown = (e: React.MouseEvent, tag: string) => {
        e.preventDefault();
        addTag(tag);
    };

    return (
        <div style={FILTER_STYLES.container}>
            <div style={FILTER_STYLES.deptHeading}>Tags</div>

            <div style={FILTER_STYLES.deptSearchWrap}>
                <input
                    ref={searchRef}
                    type="text"
                    placeholder={loading ? 'Loading…' : 'Search tags…'}
                    value={search}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    style={{
                        ...FILTER_STYLES.deptSearchInput,
                        border: searchFocused
                            ? '1px solid rgba(124,58,237,0.8)'
                            : '1px solid var(--ag-input-border-color)',
                    }}
                    disabled={loading}
                    autoComplete="off"
                />
            </div>

            {showDropdown && search.length > 0 && (
                <div style={FILTER_STYLES.deptDropdown}>
                    {suggestions.length === 0 ? (
                        <div style={FILTER_STYLES.deptEmptyMsg}>No tags found</div>
                    ) : (
                        suggestions.map((tag) => (
                            <div
                                key={tag}
                                style={{
                                    ...FILTER_STYLES.deptItem,
                                    ...(hoveredItem === tag ? FILTER_STYLES.deptItemHover : {}),
                                }}
                                role="option"
                                aria-selected={false}
                                onMouseEnter={() => setHoveredItem(tag)}
                                onMouseLeave={() => setHoveredItem(null)}
                                onMouseDown={(e) => handleItemMouseDown(e, tag)}
                            >
                                {tag}
                            </div>
                        ))
                    )}
                </div>
            )}

            {selected.length > 0 && (
                <>
                    <div style={FILTER_STYLES.deptChipsLabel}>Selected ({selected.length})</div>
                    <div style={FILTER_STYLES.deptChipsRow}>
                        {selected.map((tag) => (
                            <span key={tag} style={FILTER_STYLES.deptChip} title={tag}>
                                {tag}
                                <button
                                    type="button"
                                    style={FILTER_STYLES.deptChipRemove}
                                    onClick={() => removeTag(tag)}
                                    aria-label={`Remove ${tag}`}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                </>
            )}

            <div style={FILTER_STYLES.deptDivider} />
            <button
                type="button"
                style={{
                    ...FILTER_STYLES.deptApplyBtn,
                    ...(selected.length === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                disabled={selected.length === 0}
                onClick={handleApply}
            >
                Apply{selected.length > 0 ? ` (${selected.length})` : ''}
            </button>
            <button
                type="button"
                style={{
                    ...FILTER_STYLES.resetButton,
                    fontSize: 13,
                    ...(selected.length === 0 && model === null ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                disabled={selected.length === 0 && model === null}
                onClick={handleReset}
            >
                Reset
            </button>
        </div>
    );
};

export default TagColumnFilter;
